import bottomArrow from "../../assets/icons/bottom-arrow.png";
import { Search } from "lucide-react";
import { useState, useRef, useLayoutEffect, useEffect, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";

// زیر‌کامپوننت دراپ‌دان جستجو‌پذیر منطبق با الگو
const SearchableDropdown = ({
  label,
  options = [],
  value,
  onChange,
  isOpen,
  onToggle,
  placeholder,
}) => {
  const [search, setSearch] = useState("");
  const buttonRef = useRef(null);
  const [position, setPosition] = useState(null);

  useLayoutEffect(() => {
    if (!isOpen || !buttonRef.current) return;

    const rect = buttonRef.current.getBoundingClientRect();

    setPosition({
      top: rect.bottom + 8,
      left: rect.left,
      width: rect.width,
    });
  }, [isOpen]);

  const query = search.toLowerCase();

  const filteredOptions = options.filter((item) => {
    // پشتیبانی از فیلد title یا name
    const itemText = (item?.title || item?.name || "").toLowerCase();
    return itemText.includes(query);
  });

  const selectedOption = options.find((item) => item.id === value);
  const displayValue = selectedOption?.title || selectedOption?.name || "";

  useEffect(() => {
    if (!isOpen) return;

    const scrollY = window.scrollY;

    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";

      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  return (
    <div className="relative min-w-[100px] lg:min-w-0 shrink-0 scrollbar-hide">
      {/* Desktop Label */}
      <label className="hidden lg:block mb-1.5 text-sm font-medium text-gray-700">
        {label}
      </label>

      <button
        ref={buttonRef}
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onToggle();
        }}
        className="
          flex
          items-center
          justify-between
          h-9
          lg:h-11
          w-full
          rounded-full
          lg:rounded-xl
          border
          border-gray-200
          bg-white
          px-3
          text-[11.5px]
          lg:text-[13px]
          transition-all
          hover:border-[#49D1CD]
        "
      >
        <span
          className={`hidden lg:flex truncate flex-1 text-right ${
            displayValue ? "text-gray-800" : "text-gray-400"
          }`}
        >
          {displayValue || placeholder || `${label} را انتخاب کنید`}
        </span>
        <span
          className={`flex lg:hidden truncate flex-1 text-right ${
            displayValue ? "text-gray-800" : "text-gray-400"
          }`}
        >
          {displayValue || label}
        </span>
        <img
          src={bottomArrow}
          alt=""
          className={`w-2 transition-transform duration-300 mr-1 ${
            isOpen ? "rotate-180" : ""
          }`}
        />
      </button>

      {isOpen && position && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-[9998]"
            onClick={() => {
              setSearch("");
              onToggle();
            }}
          />

          {/* Dropdown container */}
          <div
            style={{
              position: "fixed",
              top: position.top,
              left: position.left,
              width: position.width,
              zIndex: 9999,
            }}
            className="
              rounded-xl
              border
              border-gray-200
              bg-white
              p-2
              shadow-xl
            "
          >
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={`جستجو در ${label}`}
              className="
                mb-2
                h-8
                md:h-10
                w-full
                rounded-lg
                border
                border-gray-200
                px-3
                text-[11px]
                md:text-[12px]
                outline-none
                focus:border-[#49D1CD]
              "
            />

            <div className="max-h-[260px] overflow-y-auto">
        

              {filteredOptions.length ? (
                filteredOptions.map((item) => {
                  const itemId = item.id;
                  const itemTitle = item.title || item.name;
                  const isSelected = value === itemId;

                  return (
                    <button
                      key={itemId}
                      type="button"
                      onClick={() => {
                        onChange(itemId, item);
                        setSearch("");
                        onToggle();
                      }}
                      className={`w-full rounded-lg px-3 py-1 mb-1 text-right text-[10px] lg:text-[12px] transition ${
                        isSelected
                          ? "bg-[#49D1CD]/10 text-[#49D1CD]"
                          : "text-gray-700 hover:bg-gray-50"
                      }`}
                    >
                      {itemTitle}
                    </button>
                  );
                })
              ) : (
                <div className="py-3 text-center text-sm text-gray-400">
                  موردی پیدا نشد
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

// کامپوننت اصلی بازنویسی شده با استایل یکسان با الگو
const DoctorsFilterSection = ({
  specialties = [],
  insurances = [],
  cities = [],
  selectedSpecialtyId,
  setSelectedSpecialtyId,
  selectedCity,
  setSelectedCity,
  selectedInsuranceId,
  setSelectedInsuranceId,
  tempSearch,
  setTempSearch,
  handleSearchSubmit,
}) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // کنترل باز و بسته شدن انحصاری دراپ‌دان‌ها
  const [openDropdown, setOpenDropdown] = useState(null); // 'specialty' | 'city' | 'insurance' | null

  const handleToggle = (name) => {
    setOpenDropdown(openDropdown === name ? null : name);
  };

  const updateFilters = (key, value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set(key, value);
    else params.delete(key);
    navigate(`/doctors?${params.toString()}`, { replace: true });
  };

  // چک کردن فعال بودن حداقل یکی از فیلترهای دراپ‌دان
  const isAnyFilterActive = useMemo(() => {
    return !!(selectedSpecialtyId || selectedCity || selectedInsuranceId);
  }, [selectedSpecialtyId, selectedCity, selectedInsuranceId]);

  // ریست کردن فیلترها (به جز سرچ متنی)
  const handleResetFilters = () => {
    setSelectedSpecialtyId("");
    setSelectedCity("");
    setSelectedInsuranceId("");

    const params = new URLSearchParams(searchParams);
    params.delete("specialty");
    params.delete("city");
    params.delete("insurance");
    navigate(`/doctors?${params.toString()}`, { replace: true });
  };

  return (
    <div
      className="w-full flex flex-col gap-4"
      onClick={(e) => e.stopPropagation()}
    >
      {/* یک باکس سفید با گوشه‌های گرد 2xl برای نگه داشتن سرچ و دراپ‌دان‌ها */}
      <div className="rounded-2xl bg-white p-4 shadow-sm -mt-1 md:mt-0">
        {/* سرچ فرم مستقل */}
        <form onSubmit={handleSearchSubmit} className="relative w-full">
          <Search
            className="absolute right-3 top-1/2 -translate-y-1/2 text-[#49D1CD]"
            size={18}
            strokeWidth={3}
          />
          <input
            type="text"
            value={tempSearch}
            onChange={(e) => setTempSearch(e.target.value)}
            placeholder="جستجوی پزشک ..."
            className="
              h-12
              w-full
              rounded-xl
              border
              border-[#49D1CD]/50
              pr-10
              pl-20
              text-sm
              outline-none
              transition-all
              placeholder:text-[13px]
              placeholder:text-gray-400
              focus:border-[#49D1CD]
            "
          />
          <button
            type="submit"
            className="
              absolute
              left-1.5
              top-1/2
              -translate-y-1/2
              bg-[#49D1CD]
              text-white
              px-4
              py-1.5
              rounded-lg
              text-[13px]
              hover:bg-[#3ebcb8]
              transition-colors
            "
          >
            جستجو
          </button>
        </form>

        {/* ===========================
            MOBILE FILTERS (Horizontal Scroll)
        ============================ */}
        <div
          className="
            lg:hidden
            mt-3
            -mx-4
            px-4
            overflow-x-auto
            scrollbar-hide
          "
        >
          <div
            className="
              flex
              items-start
              gap-1
              lg:gap-2
              min-w-max
              pb-1
            "
          >
            {/* تخصص */}
            <SearchableDropdown
              label="تخصص"
              placeholder="تخصص پزشک را انتخاب کنید"
              options={specialties}
              value={selectedSpecialtyId}
              isOpen={openDropdown === "specialty"}
              onToggle={() => handleToggle("specialty")}
              onChange={(val) => {
                setSelectedSpecialtyId(val);
                updateFilters("specialty", val);
              }}
            />

            {/* شهر */}
            <SearchableDropdown
              label="شهر"
              placeholder="شهر پزشک را انتخاب کنید"
              options={cities}
              value={selectedCity}
              isOpen={openDropdown === "city"}
              onToggle={() => handleToggle("city")}
              onChange={(val, item) => {
                setSelectedCity(val);
                updateFilters("city", item ? item.name : "");
              }}
            />

            {/* بیمه */}
            <SearchableDropdown
              label="بیمه"
              placeholder="بیمه مورد نظر را انتخاب کنید"
              options={insurances}
              value={selectedInsuranceId}
              isOpen={openDropdown === "insurance"}
              onToggle={() => handleToggle("insurance")}
              onChange={(val, item) => {
                setSelectedInsuranceId(val);
                updateFilters("insurance", item ? item.name : "");
              }}
            />
          </div>
        </div>

        {/* ===========================
            DESKTOP FILTERS (Vertical Stack)
        ============================ */}
        <div className="hidden lg:block mt-4">
          <div className="space-y-6">
            {/* تخصص */}
            <SearchableDropdown
              label="تخصص"
              placeholder="تخصص پزشک را انتخاب کنید"
              options={specialties}
              value={selectedSpecialtyId}
              isOpen={openDropdown === "specialty"}
              onToggle={() => handleToggle("specialty")}
              onChange={(val) => {
                setSelectedSpecialtyId(val);
                updateFilters("specialty", val);
              }}
            />

            {/* شهر */}
            <SearchableDropdown
              label="شهر"
              placeholder="شهر پزشک را انتخاب کنید"
              options={cities}
              value={selectedCity}
              isOpen={openDropdown === "city"}
              onToggle={() => handleToggle("city")}
              onChange={(val, item) => {
                setSelectedCity(val);
                updateFilters("city", item ? item.name : "");
              }}
            />

            {/* بیمه */}
            <SearchableDropdown
              label="بیمه"
              placeholder="بیمه مورد نظر را انتخاب کنید"
              options={insurances}
              value={selectedInsuranceId}
              isOpen={openDropdown === "insurance"}
              onToggle={() => handleToggle("insurance")}
              onChange={(val, item) => {
                setSelectedInsuranceId(val);
                updateFilters("insurance", item ? item.name : "");
              }}
            />

            {/* دکمه پاک کردن فیلترها مخصوص دسکتاپ */}
            {isAnyFilterActive && (
              <div className="mt-3 flex justify-center pb-1">
                <button
                  type="button"
                  onClick={handleResetFilters}
                  className="text-xs font-medium text-[#B00EB1] transition hover:opacity-80"
                >
                  پاک کردن همه فیلترها
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoctorsFilterSection;
